<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { currentUser } from "../db/auth.js";
import { supabase, useSupabase } from "../db/supabaseClient.js";
import { listSales, cancelSale, getSaleItems } from "../db/sales.js";
import { generateModernTicket } from "../utils/modernTicketPdf.js";
import CancelSaleModal from "../components/CancelSaleModal.vue";
import SuccessModal from "../components/SuccessModal.vue";
import { formatMoney } from "../utils/analyticsUtils.js";
import ChatAssistant from "../components/ChatAssistant.vue";
import { refundPayment } from "../utils/stripe.js";

const loading = ref(false);
const error = ref("");
const sales = ref([]);
const user = ref(currentUser());
const queryText = ref("");
const dateStart = ref("");
const dateEnd = ref("");
const pm = ref("");
const statusFilter = ref("");
const minTotal = ref("");
const maxTotal = ref("");
const sortBy = ref("fecha");
const sortDir = ref("desc");
const sortAscLabel = computed(() => {
  const by = String(sortBy.value || "fecha");
  if (by === "fecha") return "Más antiguo";
  if (by === "monto") return "Menor monto";
  if (by === "metodo") return "A a Z";
  if (by === "folio") return "Menor folio";
  return "Ascendente";
});
const sortDescLabel = computed(() => {
  const by = String(sortBy.value || "fecha");
  if (by === "fecha") return "Más reciente";
  if (by === "monto") return "Mayor monto";
  if (by === "metodo") return "Z a A";
  if (by === "folio") return "Mayor folio";
  return "Descendente";
});
const showSuccessModal = ref(false);
const successMessage = ref("");
const saleToCancel = ref(null);
const modalLoading = ref(false);
const modalMessage = ref("");
const modalConfirmDisabled = ref(false);

const suggestOpen = ref(false);
const suggestLoading = ref(false);
const productSuggestions = ref([]);

function openSuggestions() {
  suggestOpen.value = true;
  loadProductSuggestions(queryText.value);
}

function selectSuggestion(p) {
  queryText.value = p.name || "";
  suggestOpen.value = false;
}

async function loadProductSuggestions(q) {
  const term = String(q || "").trim();
  suggestLoading.value = true;
  try {
    if (useSupabase()) {
      let query = supabase
        .from("v_inventory_basic")
        .select("id,name,sku,image_base64,image_mime")
        .limit(10);
      if (term) query = query.or(`name.ilike.%${term}%,sku.ilike.%${term}%`);
      else query = query.order("name", { ascending: true });
      const { data } = await query;
      productSuggestions.value = (data || []).map((r) => ({
        id: r.id,
        name: r.name,
        sku: r.sku || null,
        image_base64: r.image_base64 || null,
        image_mime: r.image_mime || "image/jpeg",
      }));
    } else {
      const names = Array.from(
        new Set((sales.value || []).map((s) => s.product_name).filter(Boolean))
      );
      const rows = (
        term
          ? names.filter((n) =>
            String(n).toLowerCase().includes(term.toLowerCase())
          )
          : names
      ).slice(0, 10);
      productSuggestions.value = rows.map((n) => ({
        id: n,
        name: n,
        sku: null,
        image_base64: null,
        image_mime: "image/jpeg",
      }));
    }
  } finally {
    suggestLoading.value = false;
  }
}

watch(queryText, (val) => {
  const term = String(val || "").trim();
  if (!term) {
    productSuggestions.value = [];
    suggestOpen.value = false;
    return;
  }
  if (suggestOpen.value) loadProductSuggestions(val);
});

function isCanceledSale(s) {
  const status = String(s?.payment_status || "").toLowerCase();
  return (
    Boolean(s?.is_canceled) || status === "canceled" || status === "refunded"
  );
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    let custId = null;
    if (useSupabase()) {
      if (user.value?.email) {
        const { data } = await supabase
          .from("customers")
          .select("id_customers")
          .eq("email", user.value.email)
          .limit(1);
        custId = data?.[0]?.id_customers || null;
      }
    }
    let rows = await listSales();
    if (custId) {
      rows = rows.filter((r) => Number(r.customer_id || 0) === Number(custId));
    } else {
      const name = String(
        user.value?.full_name || user.value?.username || ""
      ).toLowerCase();
      rows = rows.filter(
        (r) => String(r.customer_name || "").toLowerCase() === name
      );
    }
    sales.value = rows;
  } catch (e) {
    error.value = e?.message || "Error al cargar compras";
  } finally {
    loading.value = false;
  }
}

onMounted(load);

function formatDate(x) {
  try {
    return new Intl.DateTimeFormat("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(x));
  } catch {
    return String(x);
  }
}
function formatDay(x) {
  try {
    if (typeof x === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x)) {
      const [yy, mm, dd] = x.split("-").map(Number);
      return new Intl.DateTimeFormat("es-MX", { dateStyle: "long" }).format(
        new Date(yy, mm - 1, dd)
      );
    }
    return new Intl.DateTimeFormat("es-MX", { dateStyle: "long" }).format(
      new Date(x)
    );
  } catch {
    return String(x);
  }
}

function dateKey(x) {
  const d = new Date(x);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}

const filtered = computed(() => {
  const q = queryText.value.trim().toLowerCase();
  let base = sales.value || [];
  if (q) {
    base = base.filter(
      (s) =>
        String(s.product_name || "")
          .toLowerCase()
          .includes(q) ||
        String(s.doc_number || "")
          .toLowerCase()
          .includes(q) ||
        String(s.payment_method || "")
          .toLowerCase()
          .includes(q)
    );
  }
  if (dateStart.value) {
    const s0 = String(dateStart.value);
    base = base.filter((s) => dateKey(s.created_at) >= s0);
  }
  if (dateEnd.value) {
    const s1 = String(dateEnd.value);
    base = base.filter((s) => dateKey(s.created_at) <= s1);
  }
  if (pm.value) {
    base = base.filter(
      (s) =>
        String(s.payment_method || "").toLowerCase() ===
        String(pm.value).toLowerCase()
    );
  }
  if (statusFilter.value) {
    const sf = String(statusFilter.value).toLowerCase();
    if (sf === "cancelados") {
      base = base.filter((s) => isCanceledSale(s));
    } else if (sf === "reembolsados") {
      base = base.filter(
        (s) => String(s.payment_status || "").toLowerCase() === "refunded"
      );
    } else if (sf === "activos") {
      base = base.filter(
        (s) =>
          !isCanceledSale(s) &&
          String(s.payment_status || "").toLowerCase() !== "refunded"
      );
    }
  }
  const min = Number(minTotal.value || 0);
  const max = Number(maxTotal.value || 0);
  if (min > 0) base = base.filter((s) => Number(s.total || 0) >= min);
  if (max > 0) base = base.filter((s) => Number(s.total || 0) <= max);
  const dir = sortDir.value === "asc" ? 1 : -1;
  const by = String(sortBy.value || "fecha");
  base = [...base].sort((a, b) => {
    if (by === "monto")
      return (Number(a.total || 0) - Number(b.total || 0)) * dir;
    if (by === "metodo")
      return (
        String(a.payment_method || "").localeCompare(
          String(b.payment_method || "")
        ) * dir
      );
    if (by === "folio") {
      const na = Number(a.doc_number);
      const nb = Number(b.doc_number);
      const an = Number.isFinite(na);
      const bn = Number.isFinite(nb);
      if (an && bn) return (na - nb) * dir;
      return (
        String(a.doc_number || "").localeCompare(String(b.doc_number || "")) *
        dir
      );
    }
    return (new Date(a.created_at) - new Date(b.created_at)) * dir;
  });
  return base;
});

const kpiCount = computed(() => filtered.value.length);
const kpiTotal = computed(() =>
  filtered.value.reduce((acc, x) => acc + Number(x.total || 0), 0)
);
const kpiRefundedCount = computed(
  () =>
    filtered.value.filter(
      (s) =>
        String(s.payment_status || "").toLowerCase() === "refunded" ||
        !!s.refund_id
    ).length
);
const kpiCanceledCount = computed(
  () => filtered.value.filter((s) => isCanceledSale(s)).length
);
const kpiRefundedTotal = computed(() =>
  filtered.value.reduce((acc, s) => acc + Number(s.refund_amount || 0), 0)
);
const grouped = computed(() => {
  const map = new Map();
  for (const s of filtered.value) {
    const key = dateKey(s.created_at);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  return Array.from(map.entries())
    .sort((a, b) => {
      const [yA, mA, dA] = a[0].split("-").map(Number);
      const [yB, mB, dB] = b[0].split("-").map(Number);
      return new Date(yB, mB - 1, dB) - new Date(yA, mA - 1, dA);
    })
    .map(([date, items]) => ({ date, items }));
});
const paymentMethods = computed(() => {
  const set = new Set(
    (sales.value || [])
      .map((s) => String(s.payment_method || "").trim())
      .filter(Boolean)
  );
  return Array.from(set);
});
function applyQuick(days) {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - days + 1);
  const toISO = (d) => d.toISOString().slice(0, 10);
  dateStart.value = toISO(start);
  dateEnd.value = toISO(now);
}

function isSameDay(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function canCancel(s) {
  const now = new Date();
  return isSameDay(s.created_at, now);
}

function statusKind(s) {
  const st = String(s?.payment_status || "").toLowerCase();
  if (st === "refunded") return "refunded";
  if (isCanceledSale(s)) return "canceled";
  return "active";
}

function statusLabel(s) {
  const k = statusKind(s);
  if (k === "refunded") return "Pedido reembolsado";
  if (k === "canceled") return "Pedido cancelado";
  return "Activo";
}

function paymentIcon(method) {
  const m = String(method || "").toLowerCase();
  if (m === "card") return "credit_card";
  if (m === "cash") return "paid";
  return "payments";
}

async function cancel(s) {
  if (!canCancel(s)) return;
  modalMessage.value = "";
  saleToCancel.value = s;
}

async function confirmCancel() {
  const s = saleToCancel.value;
  if (!s) return;
  modalLoading.value = true;
  try {
    if (String(s.payment_method || "").toLowerCase() === "card") {
      const ref = findStripePaymentRef(s);
      if (ref.intentId || ref.chargeId) {
        modalMessage.value = "Procesando reembolso a tarjeta…";
        // Usar la función refundPayment que maneja correctamente la URL en producción
        const data = await refundPayment(
          ref.intentId || undefined,
          ref.chargeId || undefined,
          Math.round(Number(s.total || 0) * 100)
        );
        if (data?.error)
          throw new Error(data.error || "Error de Stripe");
        modalMessage.value =
          "Reembolso realizado (" +
          (data?.amount
            ? "$" + (data.amount / 100).toFixed(2)
            : "monto completo") +
          "). ID: " +
          (data?.id || "-");
        modalConfirmDisabled.value = true;
        const ok = await cancelSale(Number(s.id), {
          payment_status: "refunded",
          refund_id: data?.id,
          refund_amount: Number(data?.amount || 0) / 100,
        });
        if (ok) {
          // Actualizar la venta en la lista en lugar de eliminarla
          const index = sales.value.findIndex((x) => x.id === s.id);
          if (index !== -1) {
            sales.value[index] = {
              ...sales.value[index],
              payment_status: "refunded",
              refund_id: data?.id,
              refund_amount: Number(data?.amount || 0) / 100,
              is_canceled: true,
              canceled_at: new Date().toISOString(),
            };
          } else {
            // Si no se encuentra, recargar la lista completa
            await load();
          }
          successMessage.value = "Compra cancelada y reembolsada exitosamente";
          showSuccessModal.value = true;
        } else {
          throw new Error("No se pudo cancelar la compra");
        }
      } else {
        modalMessage.value =
          "No se encontró el identificador de pago de Stripe en la venta";
        const ok = await cancelSale(Number(s.id), {
          payment_status: "canceled",
        });
        if (ok) {
          // Actualizar la venta en la lista en lugar de eliminarla
          const index = sales.value.findIndex((x) => x.id === s.id);
          if (index !== -1) {
            sales.value[index] = {
              ...sales.value[index],
              payment_status: "canceled",
              is_canceled: true,
              canceled_at: new Date().toISOString(),
            };
          } else {
            // Si no se encuentra, recargar la lista completa
            await load();
          }
          successMessage.value = "Compra cancelada exitosamente";
          showSuccessModal.value = true;
        } else {
          throw new Error("No se pudo cancelar la compra");
        }
      }
    }
    if (String(s.payment_method || "").toLowerCase() !== "card") {
      const ok = await cancelSale(Number(s.id), { payment_status: "canceled" });
      if (ok) {
        // Actualizar la venta en la lista en lugar de eliminarla
        const index = sales.value.findIndex((x) => x.id === s.id);
        if (index !== -1) {
          sales.value[index] = {
            ...sales.value[index],
            payment_status: "canceled",
            is_canceled: true,
            canceled_at: new Date().toISOString(),
          };
        } else {
          // Si no se encuentra, recargar la lista completa
          await load();
        }
        successMessage.value = "Compra cancelada exitosamente";
        showSuccessModal.value = true;
      } else {
        throw new Error("No se pudo cancelar la compra");
      }
    }
  } catch (e) {
    error.value = e?.message || "Error en cancelación";
    setTimeout(() => {
      error.value = "";
    }, 3000);
  } finally {
    modalLoading.value = false;
    saleToCancel.value = null;
  }
}

function findStripePaymentRef(s) {
  const texts = [
    String(s.payment_intent_id || ""),
    String(s.payment_charge_id || ""),
    String(s.doc_number || ""),
    String(s.notes || ""),
  ];
  let intentId = null;
  let chargeId = null;
  for (const text of texts) {
    if (!intentId) {
      const m = text.match(/pi_[A-Za-z0-9]+/);
      if (m) intentId = m[0];
    }
    if (!chargeId) {
      const c = text.match(/ch_[A-Za-z0-9]+/);
      if (c) chargeId = c[0];
    }
  }
  return { intentId, chargeId };
}

async function downloadTicket(s) {
  try {
    const items = await getSaleItems(Number(s.id));
    await generateModernTicket({
      logoUrl: "/img/sushi.png",
      title: "Sushito",
      saleId: s.id,
      items: (items || []).map((i) => ({
        name: i.product_name,
        qty: i.quantity,
        unit_price: i.unit_price,
        total: i.total,
        image: i.product_image,
        mime: i.product_image_mime,
      })),
      subtotal: Number(s.subtotal || 0),
      discount: Number(s.discount_total || 0),
      tax: Number(s.tax_total || 0),
      total: Number(s.total || 0),
      paymentMethod: String(s.payment_method || "").toLowerCase(),
      customerName: s.customer_name || null,
      date: formatDate(s.created_at),
      status: statusKind(s),
    });
  } catch { }
}
</script>

<template>
  <main class="purchases-wrap">
    <header class="purchases-top">
      <h1 class="title">Compras</h1>
      <p class="subtitle">Historial de tus compras</p>
      <div class="kpis">
        <div class="kpi orange">
          <span class="material-symbols-rounded icon">shopping_bag</span>
          <div class="kpi-val">{{ kpiCount }}</div>
          <div class="kpi-label">Compras</div>
        </div>
        <div class="kpi blue">
          <span class="material-symbols-rounded icon">request_quote</span>
          <div class="kpi-val">{{ formatMoney(kpiTotal) }}</div>
          <div class="kpi-label">Gastado</div>
        </div>
        <div class="kpi green">
          <span class="material-symbols-rounded icon">payments</span>
          <div class="kpi-val">{{ kpiRefundedCount }}</div>
          <div class="kpi-label">Reembolsados</div>
        </div>
        <div class="kpi purple">
          <span class="material-symbols-rounded icon">cancel</span>
          <div class="kpi-val">{{ kpiCanceledCount }}</div>
          <div class="kpi-label">Cancelados</div>
        </div>
      </div>
    </header>
    <section class="panel">
      <div v-if="error" class="error">{{ error }}</div>
      <div v-if="notice" class="notice">{{ notice }}</div>
      <div v-else-if="loading" class="skeleton-list">
        <div class="skeleton-row" v-for="n in 6" :key="n"></div>
      </div>
      <div v-else>
        <div class="search">
          <span class="material-symbols-rounded">search</span>
          <div class="search-wrap">
            <input class="search-input" v-model="queryText" placeholder="Buscar por producto, folio o método"
              @focus="openSuggestions" @input="suggestOpen = true" @keydown.escape="suggestOpen = false"
              @blur="setTimeout(() => (suggestOpen = false), 120)" />
            <div v-if="suggestOpen" class="suggest-backdrop" @click="suggestOpen = false"></div>
            <div v-if="suggestOpen" class="suggest-box">
              <div v-if="suggestLoading" class="suggest-loading">Buscando…</div>
              <ul v-else class="suggest-list">
                <li v-for="p in productSuggestions" :key="p.id" class="suggest-item"
                  @mousedown.prevent="selectSuggestion(p)">
                  <img v-if="p.image_base64" :src="`data:${p.image_mime};base64,${p.image_base64}`" alt=""
                    class="suggest-thumb" />
                  <div class="suggest-info">
                    <div class="suggest-name">{{ p.name }}</div>
                    <div class="suggest-sku" v-if="p.sku">{{ p.sku }}</div>
                  </div>
                </li>
                <li v-if="!productSuggestions.length && !suggestLoading" class="suggest-empty">
                  Sin resultados
                </li>
              </ul>
            </div>
          </div>
          <button v-if="queryText" class="clear-btn" @click="queryText = ''">
            <span class="material-symbols-rounded">close</span>
          </button>
        </div>

        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filter-item">
            <span class="material-symbols-rounded filter-icon">calendar_today</span>
            <div class="filter-content">
              <label class="filter-label">Desde</label>
              <input type="date" v-model="dateStart" class="filter-input" />
            </div>
          </div>

          <div class="filter-item">
            <span class="material-symbols-rounded filter-icon">event</span>
            <div class="filter-content">
              <label class="filter-label">Hasta</label>
              <input type="date" v-model="dateEnd" class="filter-input" />
            </div>
          </div>

          <div class="filter-item">
            <span class="material-symbols-rounded filter-icon">payments</span>
            <div class="filter-content">
              <label class="filter-label">Método</label>
              <select v-model="pm" class="filter-input">
                <option value="">Todos</option>
                <option v-for="m in paymentMethods" :key="m" :value="m">
                  {{ m }}
                </option>
              </select>
            </div>
          </div>

          <div class="filter-item">
            <span class="material-symbols-rounded filter-icon">info</span>
            <div class="filter-content">
              <label class="filter-label">Estado</label>
              <select v-model="statusFilter" class="filter-input">
                <option value="">Todos</option>
                <option value="activos">Activos</option>
                <option value="cancelados">Cancelados</option>
                <option value="reembolsados">Reembolsados</option>
              </select>
            </div>
          </div>

          <div class="filter-item">
            <span class="material-symbols-rounded filter-icon">attach_money</span>
            <div class="filter-content">
              <label class="filter-label">Monto mín.</label>
              <input type="number" min="0" step="0.01" v-model="minTotal" class="filter-input" placeholder="$0.00" />
            </div>
          </div>

          <div class="filter-item">
            <span class="material-symbols-rounded filter-icon">payments</span>
            <div class="filter-content">
              <label class="filter-label">Monto máx.</label>
              <input type="number" min="0" step="0.01" v-model="maxTotal" class="filter-input" placeholder="$0.00" />
            </div>
          </div>

          <div class="filter-item">
            <span class="material-symbols-rounded filter-icon">sort</span>
            <div class="filter-content">
              <label class="filter-label">Ordenar por</label>
              <div class="order-row">
                <select v-model="sortBy" class="filter-input">
                  <option value="fecha">Fecha</option>
                  <option value="monto">Monto</option>
                  <option value="metodo">Método</option>
                  <option value="folio">Folio</option>
                </select>
                <select v-model="sortDir" class="filter-input filter-input-small">
                  <option value="asc">{{ sortAscLabel }}</option>
                  <option value="desc">{{ sortDescLabel }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Filters -->
        <div class="quick-filters">
          <button class="chip-filter" @click="applyQuick(7)">
            <span class="material-symbols-rounded">today</span>
            Últimos 7 días
          </button>
          <button class="chip-filter" @click="applyQuick(30)">
            <span class="material-symbols-rounded">date_range</span>
            Últimos 30 días
          </button>
          <button class="chip-filter chip-filter-clear" @click="
            dateStart = '';
          dateEnd = '';
          pm = '';
          minTotal = '';
          maxTotal = '';
          statusFilter = '';
          ">
            <span class="material-symbols-rounded">refresh</span>
            Limpiar filtros
          </button>
        </div>

        <div v-if="!filtered.length" class="empty">
          <span class="material-symbols-rounded">inbox</span>
          <div>No hay compras que coincidan</div>
        </div>
        <div v-else class="list">
          <div v-for="g in grouped" :key="g.date" class="day-group">
            <div class="day-header">
              <span class="material-symbols-rounded">calendar_month</span>
              {{ formatDay(g.date) }}
            </div>
            <div v-for="s in g.items" :key="s.id" class="sale-card">
              <div class="sale-header">
                <div class="sale-product">
                  <div class="thumb" v-if="s.product_image">
                    <img :src="`data:${s.product_image_mime};base64,${s.product_image}`" alt="" />
                  </div>
                  <div class="thumb thumb-fallback" v-else>
                    <span class="material-symbols-rounded">shopping_cart</span>
                  </div>
                  <div class="product-info">
                    <div class="product-name">
                      {{ s.product_name || "Compra" }}
                    </div>
                    <div class="product-meta">
                      <span class="material-symbols-rounded meta-icon">schedule</span>
                      {{ formatDate(s.created_at) }}
                    </div>
                  </div>
                </div>
                <div class="sale-amount">{{ formatMoney(s.total) }}</div>
              </div>

              <div class="sale-details">
                <div class="detail-badges">
                  <span v-if="s.doc_number" class="badge badge-folio">
                    <span class="material-symbols-rounded">tag</span>
                    #{{ s.doc_number }}
                  </span>
                  <span :class="[
                    'badge',
                    'badge-payment',
                    (s.payment_method || '').toLowerCase(),
                  ]">
                    <span class="material-symbols-rounded">{{
                      paymentIcon(s.payment_method)
                      }}</span>
                    {{ s.payment_method || "-" }}
                  </span>
                  <span v-if="statusKind(s) !== 'active'" :class="['badge', 'badge-status', statusKind(s)]">
                    <span class="material-symbols-rounded">
                      {{
                        statusKind(s) === "refunded"
                          ? "currency_exchange"
                          : "cancel"
                      }}
                    </span>
                    {{ statusLabel(s) }}
                  </span>
                </div>

                <div class="sale-actions">
                  <button class="action-btn action-btn-ticket" @click="downloadTicket(s)">
                    <span class="material-symbols-rounded">receipt_long</span>
                    <span class="btn-text">Ticket</span>
                  </button>
                  <button v-if="statusKind(s) === 'active' && canCancel(s)" class="action-btn action-btn-cancel"
                    @click="cancel(s)">
                    <span class="material-symbols-rounded">cancel</span>
                    <span class="btn-text">Cancelar</span>
                  </button>
                  <span v-if="statusKind(s) === 'active' && !canCancel(s)" class="action-disabled">
                    <span class="material-symbols-rounded">block</span>
                    No cancelable
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
  <CancelSaleModal :show="!!saleToCancel" :loading="modalLoading" :message="modalMessage"
    :confirm-disabled="modalConfirmDisabled" @close="saleToCancel = null" @confirm="confirmCancel" />
  <SuccessModal :show="showSuccessModal" :message="successMessage" @close="showSuccessModal = false" />
  <ChatAssistant />
</template>

<style scoped>
/* ===== BASE LAYOUT ===== */
.purchases-wrap {
  display: grid;
  gap: 1.5rem;
  padding: clamp(1rem, 3vw, 2rem);
  min-height: 100vh;
  color: var(--text);
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== HEADER SECTION ===== */
.purchases-top {
  display: grid;
  gap: 0.5rem;
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.title {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 900;
  background: linear-gradient(135deg, var(--text), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.subtitle {
  color: var(--muted);
  font-size: clamp(0.875rem, 2vw, 1rem);
  font-weight: 500;
}

/* ===== KPI CARDS ===== */
.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.kpi {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 0.75rem;
  border-radius: 16px;
  padding: 1.25rem;
  color: #fff;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  cursor: pointer;
}

.kpi::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, transparent, rgba(255, 255, 255, 0.1));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.kpi:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.kpi:hover::before {
  opacity: 1;
}

.kpi .icon {
  color: #fff;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 14px;
  padding: 0.65rem;
  font-size: 1.75rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.kpi:hover .icon {
  transform: rotate(5deg) scale(1.1);
}

.kpi .kpi-val {
  color: #fff;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  font-weight: 900;
  letter-spacing: -0.02em;
}

.kpi .kpi-label {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi.orange {
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
}

.kpi.blue {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.kpi.green {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.kpi.purple {
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
}

/* ===== PANEL CONTAINER ===== */
.panel {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 20px;
  padding: clamp(1rem, 3vw, 1.5rem);
  backdrop-filter: blur(20px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  animation: scaleIn 0.4s ease-out 0.1s backwards;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ===== MESSAGES ===== */
.error {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border: 1.5px solid #ef4444;
  padding: 1rem;
  border-radius: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: shake 0.4s ease;
}

@keyframes shake {

  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  75% {
    transform: translateX(5px);
  }
}

.notice {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
  border: 1.5px solid #22c55e;
  padding: 1rem;
  border-radius: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* ===== LOADING SKELETON ===== */
.skeleton-list {
  display: grid;
  gap: 0.75rem;
}

.skeleton-row {
  height: 80px;
  border-radius: 16px;
  background: linear-gradient(90deg,
      rgba(198, 146, 61, 0.05),
      rgba(198, 146, 61, 0.15),
      rgba(198, 146, 61, 0.05));
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

/* ===== SEARCH BAR ===== */
.search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
  padding: 0.35rem 0.5rem;
  background: var(--panel-bg);
  border: 1.5px solid var(--border);
  border-radius: 8px;
  transition: all 0.3s ease;
  width: 240px;
}

.search:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.1);
  background: var(--surface);
}

.search .material-symbols-rounded {
  color: var(--muted);
  font-size: 1rem;
  transition: color 0.3s ease;
}

.search:focus-within .material-symbols-rounded {
  color: var(--accent);
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 500;
}

.search-input::placeholder {
  color: var(--muted);
  font-weight: 400;
}

.clear-btn {
  background: rgba(198, 146, 61, 0.1);
  border: none;
  color: var(--accent);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-btn:hover {
  color: #fff;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  font-weight: 900;
  letter-spacing: -0.02em;
}

.kpi .kpi-label {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi.orange {
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
}

.kpi.blue {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.kpi.green {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.kpi.purple {
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
}

/* ===== PANEL CONTAINER ===== */
.panel {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 20px;
  padding: clamp(1rem, 3vw, 1.5rem);
  backdrop-filter: blur(20px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  animation: scaleIn 0.4s ease-out 0.1s backwards;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ===== MESSAGES ===== */
.error {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border: 1.5px solid #ef4444;
  padding: 1rem;
  border-radius: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: shake 0.4s ease;
}

@keyframes shake {

  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  75% {
    transform: translateX(5px);
  }
}

.notice {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
  border: 1.5px solid #22c55e;
  padding: 1rem;
  border-radius: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* ===== LOADING SKELETON ===== */
.skeleton-list {
  display: grid;
  gap: 0.75rem;
}

.skeleton-row {
  height: 80px;
  border-radius: 16px;
  background: linear-gradient(90deg,
      rgba(198, 146, 61, 0.05),
      rgba(198, 146, 61, 0.15),
      rgba(198, 146, 61, 0.05));
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

/* ===== SEARCH BAR ===== */
.search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding: 0.75rem 1rem;
  background: var(--panel-bg);
  border: 1.5px solid var(--border);
  border-radius: 14px;
  transition: all 0.3s ease;
}

.search:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.1);
  background: var(--surface);
}

.search .material-symbols-rounded {
  color: var(--muted);
  font-size: 1.25rem;
  transition: color 0.3s ease;
}

.search:focus-within .material-symbols-rounded {
  color: var(--accent);
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 500;
}

.search-input::placeholder {
  color: var(--muted);
  font-weight: 400;
}

.clear-btn {
  background: rgba(198, 146, 61, 0.1);
  border: none;
  color: var(--accent);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-btn:hover {
  background: rgba(198, 146, 61, 0.2);
  transform: scale(1.06);
}

.search-wrap {
  position: relative;
  flex: 1;
}

.suggest-backdrop {
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 900;
}

.suggest-box {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  margin-top: 6px;
}

.suggest-loading {
  padding: 0.5rem 0.75rem;
  color: var(--muted);
}

.suggest-list {
  list-style: none;
  margin: 0;
  padding: 0.3rem 0;
  max-height: 260px;
  overflow-y: auto;
}

.suggest-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  color: var(--text);
}

.suggest-item:hover {
  background: #f8fafc;
}

.suggest-thumb {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--border);
}

.suggest-info {
  display: grid;
}

.suggest-name {
  font-weight: 600;
  font-size: 0.92rem;
  color: var(--text);
}

.suggest-name {
  font-size: 0.85rem;
}

.suggest-sku {
  font-size: 0.78rem;
  color: var(--muted);
}

.suggest-empty {
  padding: 0.5rem 0.75rem;
  color: var(--muted);
}

/* Dark theme via data-theme attribute */
[data-theme="dark"] .suggest-box {
  background: #1e293b;
  border-color: #334155;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .suggest-item {
  color: #e2e8f0;
}

[data-theme="dark"] .suggest-item:hover {
  background: #334155;
}

[data-theme="dark"] .suggest-thumb {
  border-color: #334155;
}

[data-theme="dark"] .suggest-name {
  color: #e2e8f0;
}

[data-theme="dark"] .suggest-sku,
[data-theme="dark"] .suggest-loading,
[data-theme="dark"] .suggest-empty {
  color: #94a3b8;
}

/* ===== FILTERS SECTION ===== */
.filters-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

/* search arriba, filtros abajo */

.filter-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--surface);
  border: 1.5px solid var(--border);
  border-radius: 14px;
  padding: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.filter-item:hover {
  border-color: rgba(198, 146, 61, 0.4);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.1);
  transform: translateY(-2px);
}

.filter-icon {
  color: var(--accent);
  font-size: 1.5rem;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.filter-item:hover .filter-icon {
  transform: scale(1.1) rotate(5deg);
}

.filter-content {
  flex: 1;
  display: grid;
  gap: 0.35rem;
  min-width: 0;
}

.filter-label {
  font-size: 0.65rem;
  color: var(--muted);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.filter-input {
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  color: var(--text);
  font-size: 0.85rem;
  font-weight: 500;
}

.filter-input:focus {
  color: var(--accent);
}

.filter-input::placeholder {
  color: var(--muted);
  opacity: 0.6;
}

.order-row {
  display: grid;
  grid-template-columns: 1fr 60px;
  gap: 0.5rem;
}

.filter-input-small {
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
}

/* ===== QUICK FILTERS ===== */
.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.chip-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 999px;
  border: 1.5px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
}

.chip-filter .material-symbols-rounded {
  font-size: 1.125rem;
}

.chip-filter:hover {
  border-color: var(--accent);
  background: rgba(198, 146, 61, 0.1);
  color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.2);
}

.chip-filter-clear {
  background: #ef4444;
  border: none;
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.25);
}

.chip-filter-clear:hover {
  background: #f87171;
  color: #ffffff;
  border: none;
}

.chip-filter:active {
  transform: translateY(0);
}

/* ===== EMPTY STATE ===== */
.empty {
  color: var(--muted);
  text-align: center;
  padding: 3rem 1rem;
  font-size: 1.125rem;
  font-weight: 500;
}

.empty .material-symbols-rounded {
  font-size: 4rem;
  display: block;
  margin: 0 auto 1rem;
  opacity: 0.5;
}

/* ===== DAY GROUPS ===== */
.day-group {
  display: grid;
  gap: 1rem;
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.day-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: sticky;
  top: 80px;
  z-index: 5;
  padding: 1rem 1.25rem;
  font-weight: 900;
  font-size: 1.125rem;
  color: var(--text);
  background: linear-gradient(90deg,
      rgba(198, 146, 61, 0.15),
      rgba(198, 146, 61, 0.05));
  border-left: 4px solid var(--accent);
  border-radius: 14px;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  letter-spacing: -0.01em;
}

.day-header .material-symbols-rounded {
  font-size: 1.5rem;
  color: var(--accent);
}

/* ===== SALE CARDS ===== */
.list {
  display: grid;
  gap: 1.25rem;
}

.sale-card {
  display: grid;
  gap: 1rem;
  border: 1.5px solid var(--border);
  background: var(--surface);
  border-radius: 18px;
  padding: 1.25rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.sale-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.05), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sale-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  border-color: rgba(198, 146, 61, 0.5);
}

.sale-card:hover::before {
  opacity: 1;
}

/* Sale Header */
.sale-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
}

.sale-product {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.thumb {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg,
      rgba(198, 146, 61, 0.15),
      rgba(240, 147, 251, 0.15));
  display: grid;
  place-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.sale-card:hover .thumb {
  transform: scale(1.05) rotate(3deg);
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-fallback .material-symbols-rounded {
  color: var(--accent);
  font-size: 2rem;
}

.product-info {
  display: grid;
  gap: 0.5rem;
  min-width: 0;
}

.product-name {
  font-weight: 900;
  font-size: 1.125rem;
  color: var(--text);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-meta {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--muted);
  font-size: 0.875rem;
  font-weight: 600;
}

.meta-icon {
  font-size: 1rem;
}

.sale-amount {
  font-weight: 900;
  font-size: 1.75rem;
  color: var(--text);
  letter-spacing: -0.02em;
  white-space: nowrap;
}

/* Sale Details */
.sale-details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}

.detail-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.badge .material-symbols-rounded {
  font-size: 1rem;
}

.badge-folio {
  background: rgba(198, 146, 61, 0.1);
  color: var(--accent);
  border: 1.5px solid rgba(198, 146, 61, 0.3);
}

.badge-payment {
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.badge-payment.cash {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
}

.badge-payment.card {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.badge-status {
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.badge-status.refunded {
  background: linear-gradient(135deg, #14b8a6 0%, #06b6d4 100%);
}

.badge-status.canceled {
  background: linear-gradient(135deg, #ef4444 0%, #f59e0b 100%);
}

/* Sale Actions */
.sale-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  border: none;
  color: #fff;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.875rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
}

.action-btn .material-symbols-rounded {
  font-size: 1.125rem;
}

.action-btn-ticket {
  background: linear-gradient(135deg, #64748b 0%, #0ea5e9 100%);
}

.action-btn-ticket:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(14, 165, 233, 0.3);
}

.action-btn-cancel {
  background: linear-gradient(135deg, #ef4444 0%, #f97316 100%);
}

.action-btn-cancel:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.3);
}

.action-btn:active {
  transform: translateY(0);
}

.action-disabled {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--muted);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.75rem 1rem;
}

.action-disabled .material-symbols-rounded {
  font-size: 1rem;
}

/* ===== RESPONSIVE DESIGN ===== */

/* Tablet */
@media (max-width: 1024px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .filters-section {
    grid-template-columns: repeat(2, 1fr);
  }

  .search {
    width: 100%;
  }

  .day-header {
    top: 70px;
  }
}

/* Mobile */
@media (max-width: 640px) {
  .purchases-wrap {
    gap: 1.25rem;
    padding: 1rem;
  }

  .title {
    font-size: 1.5rem;
  }

  .kpis {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .kpi {
    padding: 1rem;
  }

  .kpi .icon {
    font-size: 1.5rem;
    padding: 0.5rem;
  }

  .kpi .kpi-val {
    font-size: 1.25rem;
  }

  .panel {
    padding: 1rem;
    border-radius: 16px;
  }

  .filters-section {
    grid-template-columns: 1fr;
  }

  .filter-item {
    padding: 0.875rem;
  }

  .quick-filters {
    flex-direction: column;
  }

  .chip-filter {
    width: 100%;
    justify-content: center;
  }

  .day-header {
    top: 60px;
    font-size: 1rem;
    padding: 0.875rem 1rem;
  }

  .sale-card {
    padding: 1rem;
  }

  .sale-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .sale-product {
    width: 100%;
  }

  .thumb {
    width: 56px;
    height: 56px;
  }

  .product-name {
    font-size: 1rem;
  }

  .sale-amount {
    font-size: 1.5rem;
    align-self: flex-end;
  }

  .sale-details {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-badges {
    width: 100%;
  }

  .sale-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
  }

  .btn-text {
    display: inline;
  }
}

/* Extra small devices */
@media (max-width: 380px) {
  .purchases-wrap {
    padding: 0.75rem;
  }

  .kpi {
    grid-template-columns: 1fr;
    text-align: center;
    justify-items: center;
  }

  .sale-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }

  .action-btn .btn-text {
    display: inline;
  }
}
</style>
